import { useEffect, useRef, useState } from 'react'

/** True once the element has entered the viewport (fires a single time). */
function useRevealOnce(amount = 0.2) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const el = ref.current
    if (!el || visible) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: amount }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible, amount])

  return [ref, visible]
}

/** A block that fades up once, staggered by `index` inside its section. */
export function Reveal({ as: Tag = 'div', index = 0, visible, className = '', style, children }) {
  return (
    <Tag
      data-visible={visible}
      className={`m1nt-reveal ${className}`}
      style={{ '--m1nt-reveal-index': index, ...style }}
    >
      {children}
    </Tag>
  )
}

/**
 * Full-width section modelled on Morpheus `RevealSection`: eyebrow, big Bodoni
 * title, light subtitle, then the content, separated from the previous section
 * by a hairline. Reveals once as it enters the viewport.
 */
export default function Section({
  id,
  eyebrow,
  title,
  subtitle,
  actions,
  first = false,
  backdrop = null,
  className = '',
  children,
}) {
  const [ref, visible] = useRevealOnce(0.1)

  return (
    <section
      id={id}
      ref={ref}
      className={`relative scroll-mt-[calc(56px+env(safe-area-inset-top,0px))] ${first ? 'pt-[calc(56px+env(safe-area-inset-top,0px)+var(--section)*0.55)] pb-[var(--section)]' : 'border-t border-m1-line-soft py-[var(--section)]'} ${backdrop ? 'isolate overflow-hidden' : ''} ${className}`}
    >
      {backdrop}
      <div className="relative z-10 mx-auto max-w-[1200px] px-[var(--gutter)]">
        <header>
          {eyebrow && (
            <Reveal as="p" index={0} visible={visible} className="text-[14px] text-m1-muted">
              {eyebrow}
            </Reveal>
          )}
          <Reveal
            as={first ? 'h1' : 'h2'}
            index={1}
            visible={visible}
            className="mt-4 font-m1-display text-[clamp(36px,4.6vw,58px)] font-medium sm:font-normal leading-[1.04] tracking-[-0.015em] text-m1-ink"
          >
            {title}
          </Reveal>
          {subtitle && (
            <Reveal
              as="p"
              index={2}
              visible={visible}
              className="mt-6 max-w-[30em] text-[19px] font-light leading-relaxed text-m1-ink-soft"
            >
              {subtitle}
            </Reveal>
          )}
          {actions && (
            <Reveal index={3} visible={visible} className="mt-8">
              {actions}
            </Reveal>
          )}
        </header>

        {children && (
          <Reveal index={actions ? 4 : 3} visible={visible} className="mt-12 sm:mt-16">
            {children}
          </Reveal>
        )}
      </div>
    </section>
  )
}
